/** Settings panel: Audio / Graphics / Controls / Accessibility tabs. */
import type { Game } from '@core/Game';
import type { Settings } from '@core/run';
import { el } from '../dom';
import { navButton } from '../nav';
import type { UiSettings } from '../uiSettings';

export interface SettingsHost {
  game: Game;
  ui: UiSettings;
  saveUi(patch: Partial<UiSettings>): void;
  onBack(): void;
  /** Open the Controls diagram screen. */
  onControls(): void;
}

type NumKey = 'masterVolume' | 'musicVolume' | 'sfxVolume' | 'voiceVolume' | 'cameraSensitivity';
type BoolKey = 'subtitles' | 'invertY' | 'screenShake' | 'reducedMotion' | 'showFps';

export function buildSettings(host: SettingsHost, initialTab = 0): HTMLElement {
  const { game } = host;
  const s = (): Settings => game.profile.settings;
  const panel = el('div', 'panel glass settings-panel');
  const head = el('div', 'panel-head', '<h2>Settings</h2>');
  head.append(navButton('Back', 'small ghost', () => host.onBack()));
  const tabs = el('div', 'tabs');
  const pages: HTMLElement[] = [];
  const tabBtns: HTMLButtonElement[] = [];
  // One scrolling page; tabs jump to their section and follow the scroll position.
  const mark = (i: number) => tabBtns.forEach((b, j) => b.classList.toggle('active', i === j));
  const select = (i: number, smooth = true) => {
    mark(i);
    const top = pages[i].offsetTop - tabs.offsetTop - tabs.offsetHeight - 6;
    panel.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
  };
  const addTab = (name: string): HTMLElement => {
    const i = pages.length;
    const b = el('button', 'tab', name);
    b.dataset.nav = '';
    if (i === 0) b.dataset.navDefault = '';
    b.addEventListener('click', () => select(i));
    tabs.append(b);
    tabBtns.push(b);
    const page = el('div', 'tab-page active', `<h3>${name}</h3>`);
    pages.push(page);
    return page;
  };

  const row = (label: string, sub?: string): HTMLElement => {
    const r = el('div', 'setting-row', `<div class="lbl"><span>${label}</span>${sub ? `<small>${sub}</small>` : ''}</div>`);
    r.dataset.nav = '';
    return r;
  };
  const slider = (label: string, get: () => number, set: (v: number) => void, max = 1, fmt = (v: number) => `${Math.round(v * 100)}`): HTMLElement => {
    const r = row(label);
    const ctl = el('div', 'ctl');
    const input = el('input', 'slider');
    input.type = 'range';
    input.min = max === 1 ? '0' : '0.25';
    input.max = String(max);
    input.step = '0.05';
    input.value = String(get());
    const val = el('span', 'val', fmt(get()));
    const paint = () => {
      const p = ((Number(input.value) - Number(input.min)) / (Number(input.max) - Number(input.min))) * 100;
      input.style.setProperty('--p', `${p}%`);
      val.textContent = fmt(Number(input.value));
    };
    input.addEventListener('input', () => {
      set(Number(input.value));
      paint();
    });
    paint();
    ctl.append(input, val);
    r.append(ctl);
    return r;
  };
  const toggle = (label: string, sub: string | undefined, get: () => boolean, set: (v: boolean) => void): HTMLElement => {
    const r = row(label, sub);
    const t = el('label', `toggle ${get() ? 'on' : ''}`);
    const input = el('input');
    input.type = 'checkbox';
    input.checked = get();
    input.addEventListener('change', () => {
      set(input.checked);
      t.classList.toggle('on', input.checked);
      game.audio.sfx('ui_click', { volume: 0.5 });
    });
    t.addEventListener('click', (e) => e.stopPropagation());
    r.addEventListener('click', () => input.click());
    t.append(input);
    r.append(t);
    return r;
  };
  const seg = (label: string, sub: string | undefined, options: Array<[string, string]>, get: () => string, set: (v: string) => void): HTMLElement => {
    const r = row(label, sub);
    const box = el('div', 'seg');
    for (const [v, name] of options) {
      const b = el('button', get() === v ? 'active' : '', name);
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        set(v);
        box.querySelectorAll('button').forEach((x) => x.classList.toggle('active', x === b));
        game.audio.sfx('ui_click', { volume: 0.5 });
      });
      box.append(b);
    }
    r.append(box);
    return r;
  };
  const num = (k: NumKey) => (v: number) => {
    game.profile.updateSettings({ [k]: v } as Partial<Settings>);
    game.applySettings();
  };
  const bool = (k: BoolKey) => (v: boolean) => {
    game.profile.updateSettings({ [k]: v } as Partial<Settings>);
    game.applySettings();
    host.saveUi({});
  };

  const audio = addTab('Audio');
  audio.append(
    slider('Master', () => s().masterVolume, num('masterVolume')),
    slider('Music', () => s().musicVolume, num('musicVolume')),
    slider('Effects', () => s().sfxVolume, num('sfxVolume')),
    slider('Narrator', () => s().voiceVolume, num('voiceVolume')),
  );

  const gfx = addTab('Graphics');
  gfx.append(
    seg(
      'Quality',
      'Changing this reloads the game',
      [
        ['auto', 'Auto'],
        ['low', 'Low'],
        ['medium', 'Med'],
        ['high', 'High'],
      ],
      () => s().quality,
      (v) => {
        game.profile.updateSettings({ quality: v as Settings['quality'] });
        setTimeout(() => location.reload(), 250);
      },
    ),
    el(
      'div',
      'setting-row info',
      `<div class="lbl"><span>This device</span><small>${game.quality.device} · ${game.quality.tier.toUpperCase()} tier · ${game.renderSystem.renderer.getPixelRatio().toFixed(2)}× res${game.quality.bloom ? ' · bloom' : ''}${game.quality.shadows ? ' · shadows' : ''}</small></div>`,
    ),
    toggle('Show FPS', undefined, () => s().showFps, bool('showFps')),
    slider('HUD size', () => host.ui.hudScale, (v) => host.saveUi({ hudScale: v }), 1.5, (v) => `${Math.round(v * 100)}%`),
  );

  const ctl = addTab('Controls');
  ctl.append(
    slider('Camera sensitivity', () => s().cameraSensitivity, num('cameraSensitivity'), 3, (v) => v.toFixed(2)),
    toggle('Invert camera Y', undefined, () => s().invertY, bool('invertY')),
    slider('Touch button size', () => host.ui.touchScale, (v) => host.saveUi({ touchScale: v }), 1.5, (v) => `${Math.round(v * 100)}%`),
    toggle('Left-handed touch layout', 'Joystick on the right, buttons on the left', () => host.ui.leftHanded, (v) => host.saveUi({ leftHanded: v })),
    navButton('Keyboard, gamepad &amp; touch layout', 'small', () => host.onControls()),
  );

  const acc = addTab('Accessibility');
  acc.append(
    toggle('Stage explorer', 'Every form playable from the tree; skip buttons in pause + HUD', () => host.ui.explorer, (v) => host.saveUi({ explorer: v })),
    toggle('Subtitles', 'Show The Scientist’s lines', () => s().subtitles, bool('subtitles')),
    slider('Subtitle size', () => host.ui.subtitleScale, (v) => host.saveUi({ subtitleScale: v }), 1.5, (v) => `${Math.round(v * 100)}%`),
    toggle('Screen shake', undefined, () => s().screenShake, bool('screenShake')),
    toggle('Reduced motion', 'Calmer menus, instant text, fewer flashes', () => s().reducedMotion, bool('reducedMotion')),
    toggle('Colour-blind branch patterns', 'Adds stripes/dots to the mutation meter', () => host.ui.colorBlind, (v) => host.saveUi({ colorBlind: v })),
    toggle('Skip opening cinematic', undefined, () => host.ui.skipIntro, (v) => host.saveUi({ skipIntro: v })),
  );

  panel.append(head, tabs, ...pages);
  mark(initialTab);
  if (initialTab) requestAnimationFrame(() => select(initialTab, false));
  panel.addEventListener('scroll', () => {
    let cur = 0;
    pages.forEach((p, i) => {
      if (p.offsetTop - panel.scrollTop - tabs.offsetHeight - 40 <= tabs.offsetTop) cur = i;
    });
    mark(cur);
  });
  return panel;
}
